import { useState } from 'react'
import { DateTimePicker, Group, Select, Stack, TextInput, Title } from '@common/ui'
import dayjs from 'dayjs'
import useAuditLog from '../../io/useAuditLog'
import { AuditAction, AuditCategory } from '../../io/api/audit/data/AuditEntry'
import AuditLogTable from './AuditLogTable'

const CATEGORIES: ('' | AuditCategory)[] = ['', 'Entity', 'EntityType', 'Permission', 'SemanticLink']

const ACTIONS_BY_CATEGORY: Record<AuditCategory, AuditAction[]> = {
  Entity: [
    'EntityCreate',
    'EntityValueUpdate',
    'EntityLinkUpdate',
    'EntityDelete',
    'EntityRecover',
    'EntityPublish',
    'EntityInviteUser',
  ],
  EntityType: [
    'EntityTypeCreate',
    'EntityTypeNameUpdate',
    'EntityTypeFieldUpdate',
    'EntityTypeLabelUpdate',
    'EntityTypeKindUpdate',
    'EntityTypeDelete',
    'EntityTypeRecover',
  ],
  Permission: ['PermissionGrant', 'PermissionUpdate', 'PermissionRevoke'],
  SemanticLink: ['SemanticLinkCreate', 'SemanticLinkDelete'],
}

const toIsoOrUndefined = (value: string | null): string | undefined => {
  if (!value) return undefined
  const parsed = dayjs(value)
  return parsed.isValid() ? parsed.toISOString() : undefined
}

const AuditPage = () => {
  const [category, setCategory] = useState<'' | AuditCategory>('')
  const [action, setAction] = useState<'' | AuditAction>('')
  const [actor, setActor] = useState('')
  const [targetId, setTargetId] = useState('')
  const [from, setFrom] = useState<string | null>(null)
  const [to, setTo] = useState<string | null>(null)

  const { entries, loadMore, hasMore, loading, error } = useAuditLog({
    category: category || undefined,
    action: action || undefined,
    actor: actor.trim() || undefined,
    targetId: targetId.trim() || undefined,
    from: toIsoOrUndefined(from),
    to: toIsoOrUndefined(to),
  })

  const actionOptions: ('' | AuditAction)[] = category ? ['', ...ACTIONS_BY_CATEGORY[category]] : ['']

  return (
    <Stack gap="lg" p="lg">
      <Title order={2}>Audit log</Title>
      <Group gap="md" align="flex-end">
        <DateTimePicker
          label="From"
          value={from}
          onChange={setFrom}
          maxDate={to ?? undefined}
          size="sm"
          style={{ minWidth: 220 }}
          data-testid="audit-from-picker"
        />
        <DateTimePicker
          label="To"
          value={to}
          onChange={setTo}
          minDate={from ?? undefined}
          maxDate={dayjs().format('YYYY-MM-DD HH:mm')}
          size="sm"
          style={{ minWidth: 220 }}
          data-testid="audit-to-picker"
        />
        <TextInput
          size="sm"
          label="Target id"
          value={targetId}
          onChange={(e) => setTargetId(e.target.value)}
          style={{ minWidth: 320 }}
        />
        <TextInput
          size="sm"
          label="Actor (user entity id)"
          value={actor}
          onChange={(e) => setActor(e.target.value)}
          style={{ minWidth: 320 }}
        />
        <Select
          size="sm"
          label="Category"
          value={category}
          onChange={(value) => {
            setCategory((value ?? '') as '' | AuditCategory)
            setAction('')
          }}
          data={CATEGORIES.map((c) => ({ value: c, label: c || 'All' }))}
          allowDeselect={false}
          style={{ minWidth: 180 }}
        />
        <Select
          size="sm"
          label="Action"
          value={action}
          onChange={(value) => setAction((value ?? '') as '' | AuditAction)}
          data={actionOptions.map((a) => ({ value: a, label: a || 'All' }))}
          allowDeselect={false}
          disabled={!category}
          style={{ minWidth: 220 }}
        />
      </Group>
      <AuditLogTable entries={entries} loading={loading} error={error} hasMore={hasMore} loadMore={loadMore} />
    </Stack>
  )
}

export default AuditPage
